/**
 * Full-screen embed player from Netflix clone. Uses only features/movies (tmdb, servers).
 * Web: iframe; native: message that player is web-only.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {View, useWindowDimensions} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQuery} from '@tanstack/react-query'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {getTvSeasons, getSeasonEpisodes} from '#/features/movies/tmdb'
import {DEFAULT_SERVERS, filterServers} from '#/features/movies/servers'
import {IS_WEB} from '#/env'
import {STALE} from '#/state/queries'

const STORAGE_KEY_PREFIX = 'movies_feature_vidsrc_'

interface MoviesPlayerProps {
  tmdbId: number
  contentType: 'movie' | 'tv'
  title?: string
  initialSeason?: number
  initialEpisode?: number
}

function buildSrc(
  contentType: 'movie' | 'tv',
  tmdbId: number,
  serverUrl: string,
  startAtSeconds: number,
  selectedSeason: number | null,
  selectedEpisode: number | null,
): string {
  if (contentType === 'movie') {
    if (serverUrl.includes('vidsrc-embed.ru')) {
      const params = new URLSearchParams()
      params.set('autoplay', '1')
      if (startAtSeconds > 0) params.set('t', String(startAtSeconds))
      return `${serverUrl}${tmdbId}?${params}`
    }
    if (serverUrl.includes('vidsrcme.ru')) {
      const params = new URLSearchParams()
      params.set('tmdb', String(tmdbId))
      params.set('autoplay', '1')
      if (startAtSeconds > 0) params.set('t', String(startAtSeconds))
      return `${serverUrl}?${params}`
    }
    const base = `${serverUrl}${tmdbId}`
    return startAtSeconds > 0 ? `${base}?autoPlay=true&t=${startAtSeconds}` : `${base}?autoPlay=true`
  }
  if (selectedSeason == null || selectedEpisode == null) return 'about:blank'
  if (serverUrl.includes('vidsrc-embed.ru')) {
    const params = new URLSearchParams()
    params.set('season', String(selectedSeason))
    params.set('episode', String(selectedEpisode))
    params.set('autoplay', '1')
    if (startAtSeconds > 0) params.set('t', String(startAtSeconds))
    return `${serverUrl}${tmdbId}?${params}`
  }
  if (serverUrl.includes('vidsrcme.ru')) {
    const params = new URLSearchParams()
    params.set('tmdb', String(tmdbId))
    params.set('season', String(selectedSeason))
    params.set('episode', String(selectedEpisode))
    params.set('autoplay', '1')
    if (startAtSeconds > 0) params.set('t', String(startAtSeconds))
    return `${serverUrl}?${params}`
  }
  const base = `${serverUrl}${tmdbId}/${selectedSeason}/${selectedEpisode}`
  return startAtSeconds > 0 ? `${base}?autoPlay=true&t=${startAtSeconds}` : `${base}?autoPlay=true`
}

export function MoviesPlayer({
  tmdbId,
  contentType,
  title = 'Watch',
  initialSeason,
  initialEpisode,
}: MoviesPlayerProps) {
  const t = useTheme()
  const {_} = useLingui()
  const {width} = useWindowDimensions()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [currentServerIndex, setCurrentServerIndex] = useState(0)
  const [selectedSeason, setSelectedSeason] = useState<number | null>(
    contentType === 'tv' ? initialSeason ?? null : null,
  )
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(
    contentType === 'tv' ? initialEpisode ?? null : null,
  )

  const availableServers = useMemo(
    () => filterServers(DEFAULT_SERVERS, contentType),
    [contentType],
  )
  const currentServer =
    availableServers[Math.min(currentServerIndex, availableServers.length - 1)]

  const {data: seasons = []} = useQuery({
    queryKey: ['movies-feature-tv-seasons', tmdbId],
    queryFn: ({signal}) => getTvSeasons(tmdbId, signal),
    enabled: contentType === 'tv' && !!tmdbId,
    staleTime: STALE.MINUTES.FIVE,
  })
  const normalSeasons = useMemo(
    () => seasons.filter(s => s.season_number > 0),
    [seasons],
  )

  const {data: episodes = []} = useQuery({
    queryKey: ['movies-feature-tv-episodes', tmdbId, selectedSeason],
    queryFn: ({signal}) =>
      getSeasonEpisodes(tmdbId, selectedSeason!, signal),
    enabled: contentType === 'tv' && !!tmdbId && selectedSeason != null,
    staleTime: STALE.MINUTES.FIVE,
  })

  useEffect(() => {
    if (contentType !== 'tv' || normalSeasons.length === 0) return
    if (selectedSeason === null) {
      const preferred =
        initialSeason != null && normalSeasons.some(s => s.season_number === initialSeason)
          ? initialSeason
          : normalSeasons.find(s => s.season_number === 1)?.season_number ?? normalSeasons[0]?.season_number
      setSelectedSeason(preferred ?? null)
    }
  }, [contentType, normalSeasons, initialSeason, selectedSeason])

  useEffect(() => {
    if (contentType !== 'tv' || selectedSeason == null || episodes.length === 0)
      return
    if (selectedEpisode === null) {
      const preferred =
        initialEpisode != null && episodes.some(e => e.episode_number === initialEpisode)
          ? initialEpisode
          : episodes.find(e => e.episode_number === 1)?.episode_number ?? episodes[0]?.episode_number
      setSelectedEpisode(preferred ?? null)
    }
  }, [contentType, selectedSeason, episodes, initialEpisode, selectedEpisode])

  const loadPlayer = useCallback(
    (forceRefresh = false) => {
      if (!IS_WEB) return
      const iframe =
        iframeRef.current ?? (document.getElementById('moviesFeaturePlayer') as HTMLIFrameElement | null)
      if (!iframe) return
      if (contentType === 'tv' && (selectedSeason == null || selectedEpisode == null)) return

      let startAt = 0
      const key = `${STORAGE_KEY_PREFIX}${tmdbId}`
      if (!forceRefresh && typeof window !== 'undefined') {
        try {
          const raw = localStorage.getItem(key)
          if (raw) {
            const data = JSON.parse(raw) as {progress?: {watched?: number}}
            if (data?.progress?.watched) startAt = Number(data.progress.watched)
          }
        } catch (_) {}
      }

      const serverUrl = currentServer?.serverurl ?? ''
      const url = buildSrc(
        contentType,
        tmdbId,
        serverUrl,
        startAt,
        selectedSeason,
        selectedEpisode,
      )
      const isVidSrc =
        url.includes('vidsrc-embed.ru') || url.includes('vidsrcme.ru')
      if (isVidSrc) {
        iframe.src = 'about:blank'
        iframe.setAttribute('allowfullscreen', '')
        iframe.setAttribute('referrerpolicy', 'origin')
        iframe.setAttribute('frameborder', '0')
        iframe.setAttribute(
          'allow',
          'encrypted-media; fullscreen; autoplay; picture-in-picture',
        )
        setTimeout(() => {
          if (iframe && url && url !== 'about:blank') iframe.src = url
        }, 10)
      } else {
        if (url && url !== 'about:blank') iframe.src = url
      }
    },
    [
      contentType,
      tmdbId,
      selectedSeason,
      selectedEpisode,
      currentServer,
    ],
  )

  useEffect(() => {
    if (contentType === 'movie') {
      loadPlayer()
      return
    }
    if (selectedSeason != null && selectedEpisode != null) loadPlayer()
  }, [contentType, selectedSeason, selectedEpisode, currentServerIndex, loadPlayer])

  const handleRefresh = useCallback(() => {
    if (availableServers.length > 0) {
      setCurrentServerIndex(Math.floor(Math.random() * availableServers.length))
    }
  }, [availableServers.length])

  if (!IS_WEB) {
    return (
      <View style={[a.flex_1, a.justify_center, a.p_lg]}>
        <Text style={[a.text_center, t.atoms.text_contrast_high]}>
          <Trans>Video player is only available on web.</Trans>
        </Text>
      </View>
    )
  }

  const aspectRatio = 16 / 9
  const playerHeight = width / aspectRatio

  return (
    <View style={[a.flex_1]}>
      <View style={{width: '100%', height: playerHeight, backgroundColor: '#000'}}>
        <iframe
          ref={iframeRef}
          id="moviesFeaturePlayer"
          src="about:blank"
          title={_(msg`Video player`)}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
          allow="encrypted-media; fullscreen; autoplay; picture-in-picture"
          allowFullScreen
        />
      </View>
      {contentType === 'tv' && normalSeasons.length > 0 && (
        <View style={[a.px_lg, a.py_md, a.gap_sm, t.atoms.bg]}>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            <Trans>Season</Trans> {selectedSeason} · <Trans>Episode</Trans>{' '}
            {selectedEpisode}
          </Text>
          <Button
            label={_(msg`Try another server`)}
            onPress={handleRefresh}
            color="secondary"
            size="small">
            <ButtonText>
              <Trans>Try another server</Trans>
            </ButtonText>
          </Button>
        </View>
      )}
    </View>
  )
}
